<template lang="pug">
.review-gallery
  .review-gallery-head
    h4 {{ __('Customer Images') }}
    RogueButton.view-all(color="link-blue" @click="openModal(null)") {{__('View all images') }}
  Carousel(label="Customer images" prevLabel="Previous customer images" nextLabel="Next customer images")
    .photos
      //- Named on the button: off-screen thumbnails don't render their <img> (and its alt) until scrolled into view
      button.a11y.thumb-button(v-for="(photo, index) in recentPhotos" type="button" :aria-label="photoAlt(photo)" @click="openModal(index)" :key="photo.src")
        StaticImage.thumb(:src="photo.src" :alt="photoAlt(photo)")
  Modal.photo-modal(
    v-if="isModalOpen"
    @close="closeModal"
    width="1000px"
    @keydown.left="onKeyLeft"
    @keydown.right="onKeyRight"
  )
    template(#header)
      h4.modal-header.modal-header-button(v-if="selectedPhoto" @click="selectedImageIndex = null")
        Icon(name="arrow-left")
        span {{ __('All Images') }}
      h4.modal-header(v-else) {{ __('Customer Images') }}
    template(#default)
      .photo-modal-body
        .selected-photo(v-if="selectedPhoto")
          .image-wrapper
            img.image(:src="selectedPhoto.src" :alt="photoAlt(selectedPhoto)")
            ArrowNav(v-model="selectedImageIndex" :max="allPhotos.length" prevLabel="Previous customer image" nextLabel="Next customer image")
          .review-wrapper
            ReviewItem(:review="selectedPhoto.review" layout="vertical")
        .all-photos(v-show="!selectedPhoto")
          .photo-grid
            .photo-tile(
              v-for="(photo, i) in allPhotos"
              :key="photo.src"
              ref="thumb"
              @click="selectedImageIndex = i"
            )
              StaticImage.photo(:src="photo.src" :alt="photoAlt(photo)")
              .overlay
                Stars.stars(:rating="photo.review.rating")
                .time {{ photo.review.dateRelative }}
          .show-more(v-if="showLoadMore")
            RogueButton(
              color="white"
              @click="loadMorePhotoReviews"
              :disabled="isLoading"
            ) {{ __('Show More') }}
</template>
<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue'
import ArrowNav from 'sisu/components/core/ArrowNav.vue'
import ReviewItem from './review.vue'
import { useReviewsStore } from '@/js/stores/reviewsStore'
import { useRogue } from '@/js/services/Rogue'
import { reviewPhotoAlt } from '@/js/services/reviews/photoAlt'
import type { Review, ReviewImg } from '@/js/services/reviews/reviewTypes'

const props = defineProps<{
  sku: string,
}>()

const higlightedImageCount = 10

const reviewsStore = useReviewsStore()
const rogue = useRogue()

const isLoading = ref<boolean>(false)
const isModalOpen = ref<boolean>(false)
const selectedImageIndex = ref<number | null>(null)
const lastSelectedImageIndex = ref<number>(0)

const tiles = useTemplateRef<HTMLDivElement[]>('thumb')

const gallery = computed(() => reviewsStore.gallery[props.sku.toUpperCase()])
const allPhotos = computed(() => (gallery.value?.images || []))
const recentPhotos = computed(() => allPhotos.value.slice(0, higlightedImageCount))

// The number 10 here is not arbitrary. It should match the default number of reviews loaded in bazaarvoice.ts -> getGallery()
const showLoadMore = computed<boolean>(() => gallery.value.totalReviewsWithPhotos > gallery.value.loadedReviewsWithPhotos)

const selectedPhoto = computed<(ReviewImg & { review: Review }) | null>(() => selectedImageIndex.value !== null ? allPhotos.value[selectedImageIndex.value] : null)

function photoAlt(photo: ReviewImg & { review: Review }) {
  return reviewPhotoAlt(rogue, photo.caption, photo.review.author?.name)
}

function openModal(index: number | null) {
  selectedImageIndex.value = index
  isModalOpen.value = true
}

function closeModal() {
  selectedImageIndex.value = null
  isModalOpen.value = false
}

function onKeyLeft() {
  if (selectedImageIndex.value !== null && selectedImageIndex.value > 0) {
    selectedImageIndex.value -= 1
  }
}

function onKeyRight() {
  if (selectedImageIndex.value !== null && (selectedImageIndex.value + 1 < allPhotos.value.length)) {
    selectedImageIndex.value += 1
  }
}

async function loadMorePhotoReviews() {
  isLoading.value = true
  await import(/* webpackChunkName: "reviews" */'@/js/services/reviews/bazaarvoice')
    // 100 is the max number of reviews that bazaarvoice api will let use load at once
    .then(fns => fns.getGallery(props.sku, rogue, gallery.value.loadedReviewsWithPhotos, 100))
    .then(results => {
      if (results.totalLoaded > 0) {
        reviewsStore.gallery[props.sku.toUpperCase()].images = allPhotos.value.concat(results.images)
        reviewsStore.gallery[props.sku.toUpperCase()].loadedReviewsWithPhotos += results.totalLoaded
      }
    })
    .catch(err => {
      rogue.log.warn(`Error loaded photo reviews for sku ${props.sku}`, err)
    })
  isLoading.value = false
}

watch(selectedImageIndex, () => {
  if (selectedImageIndex.value === (allPhotos.value.length - 1) && showLoadMore.value) {
    loadMorePhotoReviews()
  }

  if (selectedImageIndex.value !== null) {
    lastSelectedImageIndex.value = selectedImageIndex.value
  } else {
    nextTick(() => {
      if (tiles.value?.[lastSelectedImageIndex.value]) {
        tiles.value[lastSelectedImageIndex.value].scrollIntoView()
      }
    })
  }
})

</script>
<style lang="scss" scoped>
.review-gallery {
  &-head {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    .view-all {
      margin-bottom: $space-s;
      font-size: $size-s;
    }
  }

}

.photo-modal-body {
  max-height: min(75vh, 700px);
  overflow-y: auto;
}

.photos {
  display: flex;
  flex-direction: row;
  gap: $space-s;

  .thumb {
    width: 176px;

    @include mobile() {
      width: 125px;
    }

    &:hover {
      cursor: pointer;
    }
  }
}

.modal-header {
  margin: 0;
  display: flex;
  flex-direction: row;
  gap: $space-xxs;

  &-button {
    cursor: pointer;
  }
}

.photo-grid {
  display: grid;
  grid-template-columns: 100%;
  gap: 20px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, calc(50% - 10px));
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, calc(33.3333% - 13.3333px));
  }

  @media (min-width: 1280px) {
    grid-template-columns: repeat(4, calc(25% - 15px));
  }
}

.photo-tile {
  position: relative;
  cursor: pointer;

  .overlay {
    position: absolute;
    bottom: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding: $space-xs;
    color: $color-white;

    .time, .stars {
      font-size: $size-xs;
      margin-left: auto;
    }
  }
}

.show-more {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: $space-s 0;

  &:before, &:after {
    content: '';
    background: $color-gray-light;
    height: 1px;
    flex-grow: 2;
  }

  .button {
    margin: 0 $space-s;
  }
}

.selected-photo {
  display: flex;
  gap: $space-s;
  aspect-ratio: 3 / 2;
  flex-direction: row;

  @include mobile() {
    flex-direction: column;
    aspect-ratio: auto;
  }

  @include tablet() {
    aspect-ratio: auto;
  }

  .image-wrapper {
    position: relative;
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    max-height: 100%;
    background-color: $color-gray-x-light;

    .image {
      max-width: 100%;
      max-height: 100%;
    }
  }

  .review-wrapper {
    flex: 1;

    @include tablet() {
      overflow-y: auto;
    }
  }
}
</style>
